import { createSignal, Show, For, createMemo, createEffect, onCleanup, onMount } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import { gold, qty, medal, weekLabel, esoIconUrl, ESO_ICON_FALLBACK, qualityColor } from '../lib/format.js';
import { pickItem, pickUser } from '../lib/picks.js';
import AdminFeatureBadge from '../components/AdminFeatureBadge.jsx';
import MissingGuildPlaceholder from '../components/MissingGuildPlaceholder.jsx';
import wm from '../lib/wmStore.js';

// Issue #24 — sort key map
const SORT_KEY = { gold: 'totalGold', sales: 'totalSales', volume: 'totalItems' };
const SORT_LABELS = { gold: 'By gold', sales: 'By sales', volume: 'By volume' };
const SORT_SHORT = { gold: 'GOLD', sales: 'SALES', volume: 'ITEMS' };

/**
 * Per-guild weekly stats. Two-column layout: top sellers + top items.
 * Header strip surfaces total gold / sales / items moved.
 *
 * Issue #24: toggles to show/hide each section; sort dimension picker
 * (gold / sales / volume). Sorting is client-side — no extra API call.
 *
 * Issue #275 (Sprint 16): admins see a categorized dropdown
 * (Redfur / Allied / Other) sourced from /api/v2/admin/guilds/categorized
 * so they can audit non-Redfur guilds inline. Marked with
 * <AdminFeatureBadge/> so non-admins understand the affordance is gated.
 */
export default function GuildStats(props) {
  let rootRef;
  const [guildId, setGuildId] = createSignal(props.settings?.guildId || '');
  const [weekOffset, setWeekOffset] = createSignal(Math.max(0, Number(props.settings?.weekOffset) || 0));

  // Issue #24 — visibility + sort dimension
  const [showSellers, setShowSellers] = createSignal(props.settings?.showSellers !== false);
  const [showItems,   setShowItems]   = createSignal(props.settings?.showItems !== false);
  const [sortBy,      setSortBy]      = createSignal(props.settings?.sortBy || 'gold');

  createEffect(() => {
    const wid = props.widget?.id;
    const gid = guildId();
    const wo = weekOffset();
    const sb = sortBy();
    const ss = showSellers();
    const si = showItems();
    if (!wid) return;
    const cur = props.widget?.settings || {};
    if (
      (cur.guildId || '') !== (gid || '') ||
      (cur.weekOffset || 0) !== wo ||
      (cur.sortBy || 'gold') !== sb ||
      (cur.showSellers !== false) !== ss ||
      (cur.showItems !== false) !== si
    ) {
      wm.setSettings(wid, {
        ...cur,
        guildId: gid || '',
        weekOffset: wo,
        sortBy: sb,
        showSellers: ss,
        showItems: si,
      });
    }
  });

  // Issue #275 — admin-only categorized guild list. Empty for non-admins.
  const [adminGuilds, setAdminGuilds] = createSignal(null);
  const isAdmin = () => !!props.identity?.isAdmin;
  onMount(() => {
    if (!isAdmin()) return;
    fetch('/api/v2/admin/guilds/categorized', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(j => { if (j && (j.redfur || j.allied || j.other)) setAdminGuilds(j); })
      .catch(() => { /* tolerate */ });
  });

  const { data, loading, error } = useWidgetData('guild-stats',
    () => ({ guildId: guildId() || null, weekOffset: weekOffset() }));
  const guildList = createMemo(() => data()?.guilds || []);
  const top = createMemo(() => data()?.topItems || []);
  const lb = createMemo(() => data()?.leaderboard || []);
  const totals = createMemo(() => {
    const acc = { gold: 0, sales: 0, items: 0 };
    for (const r of lb()) {
      acc.gold  += Number(r.totalGold) || 0;
      acc.sales += Number(r.totalSales) || 0;
      acc.items += Number(r.totalItems) || 0;
    }
    return acc;
  });

  // Sorted lists — client-side only, no refetch
  const sortedSellers = createMemo(() => {
    const k = SORT_KEY[sortBy()] || 'totalGold';
    return [...lb()].sort((a, b) => (b[k] || 0) - (a[k] || 0));
  });
  const sortedItems = createMemo(() => {
    const k = SORT_KEY[sortBy()] || 'totalGold';
    return [...top()].sort((a, b) => (b[k] || 0) - (a[k] || 0));
  });

  // Value formatter for the right column based on sort dimension
  function rowValue(row) {
    if (sortBy() === 'sales')  return qty(row.totalSales || 0);
    if (sortBy() === 'volume') return qty(row.totalItems || 0);
    return gold(row.totalGold || 0);
  }

  function activateControl(selector) {
    const el = rootRef?.querySelector(selector);
    if (!el) return;
    el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    el.focus?.({ preventScroll: true });
  }

  const adminGuildList = () => {
    const grouped = adminGuilds();
    if (!grouped) return [];
    return [...(grouped.redfur || []), ...(grouped.allied || []), ...(grouped.other || [])];
  };
  const selectedGuildLabel = () => {
    const id = String(guildId() || '');
    if (!id) return 'ALL REDFUR';
    const picked = data()?.guild;
    if (picked?.guildName || picked?.name) return picked.guildName || picked.name;
    const fromList = [...guildList(), ...adminGuildList()].find(g => String(g.id || g.guildID) === id);
    return fromList?.name || fromList?.guildName || `GUILD ${id}`;
  };
  const weekChromeValue = () => weekOffset() === 0 ? 'THIS WK' : `-${weekOffset()}W`;
  const sectionChip = createMemo(() => {
    if (showSellers() && showItems()) return null;
    return {
      key: 'view',
      label: 'View',
      value: showSellers() ? 'SELLERS' : showItems() ? 'ITEMS' : 'HIDDEN',
      title: 'Visible sections — click to edit',
      onActivate: () => activateControl('.v2-gs__vis-btn'),
    };
  });

  createEffect(() => {
    props.setTitleChrome?.({
      filters: [
        {
          key: 'guild',
          label: 'Guild',
          value: selectedGuildLabel(),
          title: `Guild filter: ${selectedGuildLabel()} — click to edit`,
          onActivate: () => activateControl('.v2-gs__guild-select'),
        },
        {
          key: 'week',
          label: 'Week',
          value: weekChromeValue(),
          title: `Trade week: ${weekLabel(weekOffset())} — click to edit`,
          onActivate: () => activateControl('.v2-gs__guild-bar .v2-btn-sm'),
        },
        {
          key: 'sort',
          label: 'Sort',
          value: SORT_SHORT[sortBy()] || 'GOLD',
          title: `${SORT_LABELS[sortBy()] || 'By gold'} — click to edit`,
          onActivate: () => activateControl('.v2-gs__sort-pick'),
        },
        sectionChip(),
      ],
    });
  });
  onCleanup(() => props.setTitleChrome?.(null));

  return (
    <div class="v2-widget" ref={el => rootRef = el}>
      <div class="v2-widget-hdr">{weekLabel(weekOffset())}</div>
      <div class="v2-widget__toolbar v2-gs__guild-bar">
        <Show when={adminGuilds()} fallback={
          <select class="v2-input v2-gs__guild-select" value={guildId()} onChange={(e) => setGuildId(e.currentTarget.value)}>
            <option value="">All Redfur guilds</option>
            <For each={guildList()}>{(g) => <option value={g.id}>{g.name}</option>}</For>
          </select>
        }>
          {/* Issue #275 — admin-only categorized dropdown. */}
          <label class="v2-gs__guild-pick">
            <select class="v2-input v2-gs__guild-select" value={guildId()}
              onChange={(e) => setGuildId(e.currentTarget.value)}
              title="Admin: switch between Redfur, allied, or any tracked guild."
            >
              <option value="">All Redfur guilds</option>
              <Show when={(adminGuilds()?.redfur || []).length}>
                <optgroup label="Redfur Guilds">
                  <For each={adminGuilds().redfur}>{g => <option value={g.id}>{g.name}</option>}</For>
                </optgroup>
              </Show>
              <Show when={(adminGuilds()?.allied || []).length}>
                <optgroup label="Allied Guilds">
                  <For each={adminGuilds().allied}>{g => <option value={g.id}>{g.name}</option>}</For>
                </optgroup>
              </Show>
              <Show when={(adminGuilds()?.other || []).length}>
                <optgroup label="Other Guilds (admin)">
                  <For each={adminGuilds().other}>{g => (
                    <option value={g.id}>
                      {g.name}{g.salesCount ? ` · ${g.salesCount} sales` : ''}
                    </option>
                  )}</For>
                </optgroup>
              </Show>
            </select>
            <AdminFeatureBadge tooltip="Non-Redfur guilds visible to admins only" />
          </label>
        </Show>
        <button class="v2-btn-sm" onClick={() => setWeekOffset(weekOffset() + 1)}>‹</button>
        <span class="v2-gs__week-label">{weekOffset() === 0 ? 'this wk' : `-${weekOffset()}`}</span>
        <button class="v2-btn-sm" onClick={() => setWeekOffset(Math.max(0, weekOffset() - 1))} disabled={weekOffset() === 0}>›</button>
      </div>
      {/* Issue #24 — dimension + visibility toolbar */}
      <div class="v2-widget__toolbar v2-gs__ctrl-bar">
        <select class="v2-input v2-gs__sort-pick"
          value={sortBy()}
          onChange={(e) => setSortBy(e.currentTarget.value)}
          title="Rank section by…"
        >
          <For each={Object.entries(SORT_LABELS)}>{([val, lbl]) =>
            <option value={val}>{lbl}</option>
          }</For>
        </select>
        <button
          class={`v2-btn-sm v2-gs__vis-btn${showSellers() ? '' : ' is-dim'}`}
          onClick={() => setShowSellers(v => !v)}
          title={showSellers() ? 'Hide top sellers' : 'Show top sellers'}
        >🪙 Sellers</button>
        <button
          class={`v2-btn-sm v2-gs__vis-btn${showItems() ? '' : ' is-dim'}`}
          onClick={() => setShowItems(v => !v)}
          title={showItems() ? 'Hide top items' : 'Show top items'}
        >📦 Items</button>
      </div>
      <Show when={!loading()} fallback={<div class="v2-widget-skel">Loading…</div>}>
        <Show when={!error()} fallback={<div class="v2-error">{error()}</div>}>
          <div class="v2-stat-grid">
            <div class="v2-stat v2-stat--gold">
              <span class="v2-stat__label">Gold</span>
              <span class="v2-stat__value">{gold(totals().gold)}</span>
            </div>
            <div class="v2-stat v2-stat--cyan">
              <span class="v2-stat__label">Sales</span>
              <span class="v2-stat__value">{qty(totals().sales)}</span>
            </div>
            <div class="v2-stat">
              <span class="v2-stat__label">Items</span>
              <span class="v2-stat__value">{qty(totals().items)}</span>
            </div>
          </div>
          <Show when={lb().length || top().length} fallback={
            (() => {
              // #314 — when the picked guild has no sale data this week,
              // show the thematic placeholder with a "what we know"
              // tooltip instead of a flat empty string.
              const picked = data()?.guild;
              const slug = picked?.guildName || picked?.name || null;
              return (
                <div class="v2-empty" style={{ display: 'flex', 'align-items': 'center', gap: '8px', 'flex-wrap': 'wrap' }}>
                  <span>No activity for this week.</span>
                  <Show when={slug}>
                    <MissingGuildPlaceholder guildSlug={slug} label="What we know" />
                  </Show>
                </div>
              );
            })()
          }>
            <Show when={showSellers() || showItems()} fallback={<div class="v2-empty">All sections hidden — use the toggles above.</div>}>
              <div class="v2-widget__scroll">
                <div style={{
                  display: 'grid',
                  'grid-template-columns': showSellers() && showItems() ? '1fr 1fr' : '1fr',
                  gap: 'var(--sp-3)',
                }}>
                  <Show when={showSellers()}>
                    <div class="v2-gs-section v2-gs-section--sellers">
                      <div class="v2-section-h">🪙 Top sellers</div>
                      <ol class="v2-rank-list">
                        <For each={sortedSellers().slice(0, 8)}>{(r, i) => (
                          <li class="v2-rank-row">
                            <span class="v2-rank-row__medal">{medal(i() + 1)}</span>
                            <span style={{ 'min-width': 0 }}>
                              <span class="v2-rank-row__name v2-pick v2-pick--user" onClick={() => pickUser(r.sellerName || r.tag)}>{r.sellerName || r.tag || '—'}</span>
                              <span class="v2-rank-row__sub">{qty(r.totalSales || 0)} sales · {qty(r.totalItems || 0)} items</span>
                            </span>
                            <span class="v2-rank-row__value">{rowValue(r)}</span>
                          </li>
                        )}</For>
                      </ol>
                    </div>
                  </Show>
                  <Show when={showItems()}>
                    <div class="v2-gs-section v2-gs-section--items">
                      <div class="v2-section-h">📦 Top items</div>
                      <ol class="v2-rank-list">
                        <For each={sortedItems().slice(0, 8)}>{(it, i) => (
                          <li class="v2-rank-row">
                            <span class="v2-rank-row__medal">{medal(i() + 1)}</span>
                            <Show when={it.itemIcon}>
                              <img class="v2-item-icon v2-item-icon--xs" src={esoIconUrl(it.itemIcon)} alt=""
                                onError={(e) => { e.currentTarget.src = ESO_ICON_FALLBACK; }} />
                            </Show>
                            <span style={{ 'min-width': 0 }}>
                              <span class="v2-rank-row__name v2-pick v2-pick--item" data-icon={it.itemIcon || ''}
                                style={it.quality ? { color: qualityColor(it.quality) } : undefined}
                                onClick={() => pickItem(it.itemName, { icon: it.itemIcon })}>{it.itemName || '—'}</span>
                              <span class="v2-rank-row__sub">
                                {qty(it.totalSales || 0)} sales{it.totalItems ? ` · ${qty(it.totalItems)} items` : ''}
                              </span>
                            </span>
                            <span class="v2-rank-row__value">{rowValue(it)}</span>
                          </li>
                        )}</For>
                      </ol>
                    </div>
                  </Show>
                </div>
              </div>
            </Show>
          </Show>
        </Show>
      </Show>
    </div>
  );
}
